import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useV2GetApprovalLevelQuery,
  useV2ListCollaboratorsForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  AdminFormPage,
  useAdminFormPersistence,
  saveErrorToast,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { BusinessApprovalForm } from "../BusinessApprovalForm/BusinessApprovalForm";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import { extractApprovers } from "../api/useApprovalLevelsList";
import type {
  ApprovalLevelFormValues,
  ApprovalTrigger,
} from "../types/ApprovalLevel";

const FORM_ID = "business-approval-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useV2GetApprovalLevelQuery({ id: Number(id) });
  const { data: usersData } = useV2ListCollaboratorsForAccountQuery({});
  const { update } = useApprovalLevelMutations();
  const [submitting, setSubmitting] = useState(false);
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ApprovalLevelFormValues>();

  const initial = useMemo<ApprovalLevelFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    const attrs = record.attributes ?? {};
    return {
      name: attrs.name ?? "",
      signaturesRequired: attrs["signatures-required"] ?? 1,
      submitterMayApprove: !!attrs["submitter-may-approve"],
      limitApprovalsToTeam: !!attrs["limit-approvals-to-team"],
      triggers: (attrs.triggers ?? []) as ApprovalTrigger[],
      approvers: extractApprovers(attrs.approvers),
    };
  }, [data]);

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name ?? u.attributes?.identifier ?? "",
      })),
    [usersData]
  );

  if (isLoading || !initial) return <ScopeStackSpinner />;

  const recordName =
    (data?.data as any)?.attributes?.name || "Business Approval";

  const handleSubmit = async (values: ApprovalLevelFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      markSaved(values, "Business approval updated.");
    } catch (err) {
      console.error("Failed to update business approval", err);
      setToast(saveErrorToast(err, "business approval"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title={`Edit ${recordName}`}
        formId={FORM_ID}
        onCancel={() => navigate("/admin/approval_levels")}
        isSubmitting={submitting}
      >
        <BusinessApprovalForm
          key={formKey}
          id={FORM_ID}
          initialValues={savedInitial ?? initial}
          availableUsers={availableUsers}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
